import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useCreateProject } from "@/hooks/useProjects";
import { formatDateForInput } from "@/lib/date-utils";

const createProjectSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name is too long"),
});

type CreateProjectFormValues = z.infer<typeof createProjectSchema>;

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startDate?: Date | null;
  endDate?: Date | null;
  onSuccess?: (projectId: string) => void;
}

export function CreateProjectDialog({
  open,
  onOpenChange,
  startDate,
  endDate,
  onSuccess,
}: CreateProjectDialogProps) {
  const createProject = useCreateProject();

  const form = useForm<CreateProjectFormValues>({
    resolver: zodResolver(createProjectSchema),
    defaultValues: {
      name: "",
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        name: "",
      });
    }
  }, [open, form]);

  const handleSubmit = (values: CreateProjectFormValues) => {
    // The range selected in the calendar applies to the project, event and
    // planning dates alike.
    const start = startDate ? formatDateForInput(startDate) : null;
    const end = endDate ? formatDateForInput(endDate) : null;
    createProject.mutate(
      {
        name: values.name,
        status: "initial_contact",
        start_date: start,
        end_date: end,
        event_start_date: start,
        event_end_date: end,
        planning_start_date: start,
        planning_end_date: end,
      },
      {
        onSuccess: (data) => {
          onOpenChange(false);
          form.reset();
          onSuccess?.(data.id);
        },
      },
    );
  };

  const handleClose = () => {
    onOpenChange(false);
    form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Project</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project Name *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter project name"
                      {...field}
                      autoFocus
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createProject.isPending || createProject.isDisabled}
                title={
                  createProject.isDisabled ? "Offline, read-only" : undefined
                }
              >
                {createProject.isPending ? "Creating..." : "Create Project"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
